import React, { useState, useEffect } from 'react';
import { auth } from '@/firebase';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MemberLoginPage = () => {
    const navigate = useNavigate();
    const { toast } = useToast();

    const [step, setStep] = useState('phone'); // 'phone' or 'otp'
    const [countryCode, setCountryCode] = useState('+33');
    const [localPhone, setLocalPhone] = useState('');
    const [phone, setPhone] = useState(''); // Final formatted phone for display/auth
    const [otp, setOtp] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [loading, setLoading] = useState(false);
    const [confirmationResult, setConfirmationResult] = useState(null);

    // Initialize Recaptcha
    useEffect(() => {
        const initRecaptcha = async () => {
            const { RecaptchaVerifier } = await import('firebase/auth');
            // Ensure element exists before init
            if (!document.getElementById('sign-in-button')) return;

            if (!window.recaptchaVerifier) {
                window.recaptchaVerifier = new RecaptchaVerifier(auth, 'sign-in-button', {
                    'size': 'invisible',
                    'siteKey': process.env.REACT_APP_RECAPTCHA_SITE_KEY,
                    'callback': () => { }
                });
            }
        };

        initRecaptcha();

        return () => {
            // Cleanup on unmount to prevent stale instances
            if (window.recaptchaVerifier) {
                try {
                    window.recaptchaVerifier.clear();
                    window.recaptchaVerifier = null;
                } catch (e) { console.warn(e); }
            }
        };
    }, []);

    const handleRequestCode = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg('');

        try {
            // Format phone number
            let formattedLocal = localPhone.trim();

            // Remove spaces, dashes, parentheses first to have a clean string
            formattedLocal = formattedLocal.replace(/[\s-()]/g, '');

            // Check if it starts with the selected country code (with or without +)
            // e.g. User selected +33 and pasted +336... or 336...
            const codeWithoutPlus = countryCode.replace('+', '');

            if (formattedLocal.startsWith(countryCode)) {
                formattedLocal = formattedLocal.substring(countryCode.length);
            } else if (formattedLocal.startsWith(codeWithoutPlus)) {
                formattedLocal = formattedLocal.substring(codeWithoutPlus.length);
            }

            // Remove leading 0 if present (common in France/Comoros/etc)
            if (formattedLocal.startsWith('0')) {
                formattedLocal = formattedLocal.substring(1);
            }

            const fullPhoneNumber = `${countryCode}${formattedLocal}`;
            setPhone(fullPhoneNumber); // Store for step 2 display

            const { signInWithPhoneNumber } = await import("firebase/auth");
            // AppVerifier
            const appVerifier = window.recaptchaVerifier;

            const confirmation = await signInWithPhoneNumber(auth, fullPhoneNumber, appVerifier);
            setConfirmationResult(confirmation);
            setStep('otp');
            toast({ title: "Code envoyé", description: "Veuillez vérifier vos SMS." });
        } catch (error) {
            console.error(error);
            const msg = `Erreur: ${error.code || 'Unknown'} - ${error.message}`;
            setErrorMsg(msg);
            toast({
                title: "Erreur SMS",
                description: msg,
                variant: "destructive"
            });
            // Try to clear recaptcha validation if possible, or just ignore for now to prevent crash
            if (window.recaptchaVerifier) {
                try {
                    window.recaptchaVerifier.clear();
                    window.recaptchaVerifier = null; // Force re-init on next use
                } catch (e) { console.warn("Recaptcha clear error", e); }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // 1. Verify with Firebase
            const result = await confirmationResult.confirm(otp);
            const user = result.user;
            const idToken = await user.getIdToken();

            // 2. Send to Backend
            const res = await axios.post(`${API}/auth/firebase/login`, { id_token: idToken });
            const { access_token, association_id } = res.data;

            localStorage.setItem('hafani_member_token', access_token);

            toast({ title: "Connexion réussie" });
            navigate(association_id ? '/dashboard' : '/choisir-communaute');

        } catch (err) {
            console.error(err);
            toast({
                title: "Code invalide",
                description: "Le code est incorrect ou une erreur est survenue.",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#EAE8E4] font-sans antialiased flex flex-col">

            {/* Minimal header */}
            <header className="flex items-center justify-between px-6 sm:px-10 py-5">
                <Link to="/" className="flex flex-col items-start group">
                    <img
                        src="/assets/logo.png"
                        alt="Hafani"
                        className="h-9 w-auto object-contain transition-transform group-hover:scale-105"
                    />
                    <span className="text-[9px] text-emerald-700 font-medium mt-0.5 tracking-wide">
                        Hafani, une solution pensée pour notre fin
                    </span>
                </Link>
                <Link
                    to="/"
                    className="text-sm text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1 group"
                >
                    <span className="transition-transform group-hover:-translate-x-0.5">←</span> Accueil
                </Link>
            </header>

            {/* Main */}
            <main className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md space-y-8">

                    {/* Title */}
                    <div className="text-center">
                        <h1 className="font-serif text-4xl text-slate-900 mb-2">Espace Particuliers</h1>
                        <p className="text-slate-500 text-sm font-light">
                            Connectez-vous pour accéder à votre communauté.
                        </p>
                    </div>

                    {/* Card */}
                    <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
                        <div className="mb-6">
                            <p className="font-semibold text-slate-900 text-base">
                                {step === 'phone' ? 'Connexion par SMS' : 'Vérification'}
                            </p>
                            <p className="text-slate-500 text-sm font-light mt-1">
                                {step === 'phone'
                                    ? "Entrez votre numéro pour recevoir un code d'accès."
                                    : `Code envoyé au ${phone}`}
                            </p>
                        </div>

                        {/* ReCAPTCHA — must stay in DOM */}
                        <div id="sign-in-button"></div>

                        {step === 'phone' ? (
                            <form onSubmit={handleRequestCode} className="space-y-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                                        Téléphone
                                    </Label>
                                    <div className="flex gap-2">
                                        <Select value={countryCode} onValueChange={setCountryCode}>
                                            <SelectTrigger className="w-[110px] h-12 rounded-xl border-slate-200 bg-slate-50 text-sm">
                                                <SelectValue placeholder="Code" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="+33">🇫🇷 +33</SelectItem>
                                                <SelectItem value="+269">🇰🇲 +269</SelectItem>
                                                <SelectItem value="+262">🇾🇹 +262</SelectItem>
                                                <SelectItem value="+44">🇬🇧 +44</SelectItem>
                                                <SelectItem value="+1">🇺🇸 +1</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Input
                                            id="phone"
                                            type="tel"
                                            placeholder="06 12 34 56 78"
                                            value={localPhone}
                                            onChange={(e) => setLocalPhone(e.target.value)}
                                            required
                                            className="flex-1 h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-emerald-500 text-sm"
                                        />
                                    </div>
                                </div>

                                {errorMsg && (
                                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs whitespace-pre-wrap">
                                        {errorMsg}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    className="w-full bg-slate-900 hover:bg-emerald-800 text-white rounded-full py-6 text-sm font-medium tracking-wide transition-all duration-300 disabled:opacity-60"
                                    disabled={loading}
                                >
                                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Recevoir le code
                                </Button>
                            </form>
                        ) : (
                            <form onSubmit={handleVerifyCode} className="space-y-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="otp" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                                        Code de vérification
                                    </Label>
                                    <Input
                                        id="otp"
                                        type="text"
                                        placeholder="123456"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        required
                                        maxLength={6}
                                        className="h-14 rounded-xl border-slate-200 bg-slate-50 text-center font-serif text-3xl tracking-widest focus-visible:ring-emerald-500"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full bg-slate-900 hover:bg-emerald-800 text-white rounded-full py-6 text-sm font-medium tracking-wide transition-all duration-300 disabled:opacity-60"
                                    disabled={loading}
                                >
                                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Vérifier le code
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="w-full text-slate-500 hover:text-slate-900 text-sm rounded-full"
                                    onClick={() => setStep('phone')}
                                >
                                    Changer de numéro
                                </Button>
                            </form>
                        )}
                    </div>

                    {/* Pas encore membre */}
                    <div className="text-center space-y-3">
                        <p className="text-sm text-slate-500 font-light">Pas encore membre ?</p>
                        <Button
                            asChild
                            variant="outline"
                            className="rounded-full border-slate-300 text-slate-700 hover:bg-white hover:border-slate-400 text-sm px-6 transition-all duration-200"
                        >
                            <Link to="/">
                                Découvrir Hafani <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                        </Button>
                    </div>
                </div>
            </main>

            {/* Footer minimal */}
            <footer className="py-6 text-center">
                <p className="text-xs text-slate-400 font-light">
                    &copy; {new Date().getFullYear()} Hafani Management and Solutions
                </p>
            </footer>
        </div>
    );
};
